<template>
    <div class="plan-adhesion-editor" @contextmenu="openContextMenu">
        <tiptap-vuetify
            :value="value"
            :extensions="extensions"
            :native-extensions="nativeExtensions"
            :disabled="disabled"
            :min-height="minHeightCss"
            :card-props="{ flat: true, outlined: true }"
            placeholder="Escreva o texto. Clique com o botão direito para inserir um campo dinâmico."
            @input="$emit('input', $event)"
            @init="onInit">
            <template #toolbar-after>
                <div v-if="!disabled" class="d-flex align-center px-2 pb-2">
                    <v-btn small outlined @click="openButtonMenu">
                        <v-icon left small>mdi-code-braces</v-icon>
                        Inserir campo
                    </v-btn>
                    <span class="text-caption grey--text ml-3">ou clique com o botão direito no texto</span>
                </div>
            </template>
        </tiptap-vuetify>

        <v-menu v-model="menu.open" :position-x="menu.x" :position-y="menu.y" absolute offset-y
            :close-on-content-click="false" max-height="420" min-width="320">
            <v-card>
                <v-card-text class="pb-0">
                    <v-text-field v-model="menu.search" dense outlined hide-details autofocus clearable
                        prepend-inner-icon="mdi-magnify" placeholder="Buscar campo"></v-text-field>
                </v-card-text>
                <v-list dense class="plan-adhesion-editor__list">
                    <template v-for="group in filteredCatalog">
                        <v-subheader :key="`g-${group.group}`">{{ group.group }}</v-subheader>
                        <v-list-item v-for="field in group.fields" :key="field.key" @click="insertField(field)">
                            <v-list-item-content>
                                <v-list-item-title>{{ field.label }}</v-list-item-title>
                                <v-list-item-subtitle class="text-caption">{{ field.block ? "Bloco de texto" : field.key }}</v-list-item-subtitle>
                            </v-list-item-content>
                        </v-list-item>
                    </template>
                    <v-list-item v-if="!filteredCatalog.length">
                        <v-list-item-content class="grey--text">Nenhum campo encontrado</v-list-item-content>
                    </v-list-item>
                </v-list>
            </v-card>
        </v-menu>
    </div>
</template>

<script>
import {
    TiptapVuetify, Heading, Bold, Italic, Strike, Underline, Paragraph, BulletList, OrderedList, ListItem,
    Blockquote, HardBreak, HorizontalRule, History,
} from "tiptap-vuetify";
import PlaceholderNode from "./placeholder-node";

// Editor do termo de adesao (e das opcoes de pagamento). Campos dinamicos entram
// pelo menu de contexto (botao direito) ou pelo botao "Inserir campo"; o
// catalogo vem da API (/placeholders), unico para AJ e recuperanda.
export default {
    name: "PlanAdhesionEditor",

    components: { TiptapVuetify },

    props: {
        value: { type: String, default: "" },
        catalog: { type: Array, default: () => [] },
        excludeKeys: { type: Array, default: () => [] },
        disabled: { type: Boolean, default: false },
        minHeight: { type: [String, Number], default: 320 },
    },

    data: () => ({
        editor: null,
        nativeExtensions: [new PlaceholderNode()],
        extensions: [
            History, Bold, Italic, Underline, Strike, Paragraph,
            [Heading, { options: { levels: [1, 2, 3] } }],
            BulletList, OrderedList, ListItem, Blockquote, HorizontalRule, HardBreak,
        ],
        menu: { open: false, x: 0, y: 0, search: "", clickPosition: null },
    }),

    computed: {
        // O tiptap-vuetify le a altura como texto com unidade ("320px").
        minHeightCss() {
            return typeof this.minHeight === "number" || /^\d+$/.test(String(this.minHeight)) ? `${this.minHeight}px` : String(this.minHeight);
        },
        filteredCatalog() {
            const term = String(this.menu.search || "").toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");
            const matches = (text) => String(text || "").toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "").includes(term);
            return this.catalog
                .map((group) => ({
                    ...group,
                    fields: group.fields.filter((field) => !this.excludeKeys.includes(field.key)
                        && (!term || matches(field.label) || matches(field.key) || matches(group.group))),
                }))
                .filter((group) => group.fields.length);
        },
    },

    methods: {
        onInit({ editor }) {
            this.editor = editor;
        },

        openContextMenu(event) {
            if (this.disabled || !this.editor || !event.target.closest(".ProseMirror")) return;
            event.preventDefault();
            const position = this.editor.view.posAtCoords({ left: event.clientX, top: event.clientY });
            this.showMenu(event.clientX, event.clientY, position ? position.pos : null);
        },

        openButtonMenu(event) {
            const rect = event.currentTarget.getBoundingClientRect();
            this.showMenu(rect.left, rect.bottom, null);
        },

        showMenu(x, y, clickPosition) {
            this.menu = { open: false, x, y, search: "", clickPosition };
            this.$nextTick(() => { this.menu.open = true; });
        },

        insertField(field) {
            if (!this.editor) return;
            const { from, to } = this.editor.state.selection;
            const clicked = this.menu.clickPosition;
            // Clique direito fora da selecao atual move o cursor para o ponto clicado.
            if (clicked !== null && (from === to || clicked < from || clicked > to)) {
                this.editor.setSelection(clicked, clicked);
            }
            this.editor.commands.placeholder({ key: field.key, label: field.label });
            this.menu.open = false;
            this.editor.focus();
        },
    },
};
</script>

<style>
.plan-adhesion-field {
    display: inline-block;
    padding: 0 8px;
    margin: 0 1px;
    border-radius: 12px;
    background: #ede7f6;
    color: #4a148c;
    font-size: 0.92em;
    font-weight: 600;
    line-height: 1.6;
    white-space: nowrap;
    cursor: default;
}

.plan-adhesion-field.ProseMirror-selectednode {
    outline: 2px solid #7857a8;
}

.plan-adhesion-editor__list {
    max-height: 340px;
    overflow-y: auto;
}
</style>
